import React, { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { getPublishReadiness, type ReviewCatch, type ChampionStatus } from '@shared/adjudication';

const client = generateClient({ authMode: 'userPool' });

export interface PublishChecklistEvent {
  id: string;
  title?: string | null;
  adjudicatedAt?: string | null;
  publishedAt?: string | null;
  mysteryFishSpecies?: string | null;
  mysteryFishSize?: string | number | null;
  seniorChampionName?: string | null;
  juniorChampionName?: string | null;
}

interface ChampionStandings {
  seniorStandings: any[];
  juniorStandings: any[];
}

interface PublishChecklistDialogProps {
  event: PublishChecklistEvent;
  /**
   * The event's catches (AI fields already merged) and the champion
   * standings, when the caller already has them (AdjudicationDashboard).
   * Omit both to have the dialog load them itself (CompetitionSetup), the
   * same way AdjudicationDashboard does: paginated CatchSubmission.list
   * filtered by eventId, CatchVerification merge, calculateLeaderboard for
   * championPoints.
   */
  submissions?: ReviewCatch[];
  championStandings?: ChampionStandings;
  onClose: () => void;
  /** Called after a successful publish, so the caller can refresh and re-render. */
  onPublished: () => void | Promise<void>;
}

function championStatusFor(event: PublishChecklistEvent, standings: ChampionStandings): ChampionStatus {
  return {
    seniorRequired: standings.seniorStandings.length > 0,
    juniorRequired: standings.juniorStandings.length > 0,
    seniorConfirmed: !!event.seniorChampionName,
    juniorConfirmed: !!event.juniorChampionName,
  };
}

/** Every catch for the event (all pages), with its AI verification merged on. The same approach AdjudicationDashboard uses. */
async function loadSubmissionsSelf(eventId: string): Promise<ReviewCatch[]> {
  let subs: any[] = [];
  let subsToken: string | null = null;
  do {
    const page: any = await (client.models as any).CatchSubmission.list({
      filter: { eventId: { eq: eventId } },
      limit: 1000,
      ...(subsToken ? { nextToken: subsToken } : {}),
    });
    subs = subs.concat((page.data || []).filter(Boolean));
    subsToken = page.nextToken ?? null;
  } while (subsToken);

  try {
    let verRows: any[] = [];
    let verToken: string | null = null;
    do {
      const verPage: any = await (client.models as any).CatchVerification.list({
        limit: 2000,
        ...(verToken ? { nextToken: verToken } : {}),
      });
      verRows = verRows.concat(verPage.data || []);
      verToken = verPage.nextToken ?? null;
    } while (verToken);
    const verByCatch: Record<string, any> = {};
    for (const v of verRows.filter(Boolean)) if (v.catchId) verByCatch[v.catchId] = v;
    for (const s of subs) {
      const v = verByCatch[s.id];
      if (v) {
        s.aiSuspicionScore = v.aiSuspicionScore;
        s.aiFlags = v.aiFlags;
      }
    }
  } catch {
    /* AI verification is advisory; carry on without it */
  }
  return subs as ReviewCatch[];
}

async function loadChampionStandingsSelf(eventId: string): Promise<ChampionStandings> {
  try {
    const lb = await (client.queries as any).calculateLeaderboard({ eventId });
    const data = parseLambdaResponse<{ championPoints?: ChampionStandings }>(lb?.data);
    return data?.championPoints || { seniorStandings: [], juniorStandings: [] };
  } catch {
    return { seniorStandings: [], juniorStandings: [] };
  }
}

/**
 * Publish checklist (#144 R5.2). Shows getPublishReadiness's blockers and
 * warnings, states exactly what becomes public, and confirms with a button
 * naming the competition. On confirm, calls the existing adjudicateCatch
 * `actionType: 'PUBLISH'`. The server's success:false error
 * and blockers are shown inside the dialog; the dialog stays open on failure
 * so the admin can resolve them without losing context.
 */
const PublishChecklistDialog: React.FC<PublishChecklistDialogProps> = ({
  event,
  submissions,
  championStandings,
  onClose,
  onPublished,
}) => {
  const selfLoad = submissions === undefined && championStandings === undefined;
  const [loading, setLoading] = useState(selfLoad);
  const [loadedSubmissions, setLoadedSubmissions] = useState<ReviewCatch[]>(submissions || []);
  const [loadedStandings, setLoadedStandings] = useState<ChampionStandings>(
    championStandings || { seniorStandings: [], juniorStandings: [] },
  );
  const [publishing, setPublishing] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverBlockers, setServerBlockers] = useState<string[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!selfLoad) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [subs, standings] = await Promise.all([
        loadSubmissionsSelf(event.id),
        loadChampionStandingsSelf(event.id),
      ]);
      if (!cancelled) {
        setLoadedSubmissions(subs);
        setLoadedStandings(standings);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, selfLoad]);

  // Focus trap, Escape to close, focus the close button on open, restore
  // focus to the opener on close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = dialogRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [onClose]);

  const champions = championStatusFor(event, loadedStandings);
  const readiness = loading
    ? { ready: false, blockers: [], warnings: [] }
    : getPublishReadiness({
        event: {
          ...event,
          mysteryFishSize: event.mysteryFishSize == null ? null : Number(event.mysteryFishSize),
        },
        catches: loadedSubmissions,
        champions,
      });

  const title = event.title || 'this competition';
  const confirmDisabled = loading || publishing || !readiness.ready;

  const handleConfirm = async () => {
    if (!readiness.ready) return;
    setPublishing(true);
    setServerError(null);
    setServerBlockers([]);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'PUBLISH',
        eventId: event.id,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; blockers?: string[] }>(result?.data);
      if (!data?.success) {
        setServerError(data?.error || 'Failed to publish results');
        setServerBlockers(data?.blockers || []);
        setPublishing(false);
        return;
      }
      await onPublished();
      onClose();
    } catch (err: any) {
      setServerError(err.message || 'Failed to publish results');
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="publish-checklist-title"
          className="relative w-full max-w-lg rounded-lg bg-white p-4 shadow-xl sm:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id="publish-checklist-title" className="text-lg font-bold text-gray-900">
              Publish results
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="min-h-[44px] min-w-[44px] shrink-0 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <div aria-live="polite" className="sr-only">
            {loading
              ? 'Loading publish checklist.'
              : readiness.ready
                ? 'Ready to publish.'
                : `${readiness.blockers.length} blockers must be resolved before publishing.`}
          </div>

          {loading ? (
            <p className="mt-4 text-sm text-gray-600">Loading checklist…</p>
          ) : (
            <>
              <p className="mt-3 text-sm text-gray-700">
                Publishing <strong>{title}</strong> makes this public:
              </p>
              <ul className="mt-2 list-inside list-disc text-sm text-gray-700">
                <li>The results and placings</li>
                <li>The winners and champions</li>
                <li>The mystery fish reveal</li>
              </ul>

              {readiness.blockers.length > 0 && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-semibold text-red-800">
                    Not ready to publish ({readiness.blockers.length}):
                  </p>
                  <ul className="mt-1 list-inside list-disc text-sm text-red-700">
                    {readiness.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              {readiness.warnings.length > 0 && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-800">Before you publish:</p>
                  <ul className="mt-1 list-inside list-disc text-sm text-amber-700">
                    {readiness.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {serverError && (
                <div role="alert" aria-live="assertive" className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-semibold text-red-800">{serverError}</p>
                  {serverBlockers.length > 0 && (
                    <ul className="mt-1 list-inside list-disc text-sm text-red-700">
                      {serverBlockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  )}
                </div>
              )}

              {confirmDisabled && !loading && !publishing && readiness.blockers.length > 0 && (
                <p className="mt-3 text-sm text-gray-600">
                  Resolve every blocker above before you can publish.
                </p>
              )}

              <div className="mt-5 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="min-h-[44px] rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                  aria-disabled={confirmDisabled}
                  className="min-h-[44px] rounded-md bg-fishing-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {publishing ? 'Publishing…' : `Publish ${title}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishChecklistDialog;
